import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Tooltip,
} from "@material-ui/core";
import { CloudUpload, Refresh, GetApp } from "@material-ui/icons";
import axios from "axios";
import moment from "moment";
import Loader from "./Loader";

const ConfigBackupList = ({ deviceUuid, setError, setMessage }) => {
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [createBackupDialog, setCreateBackupDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchBackups = useCallback(() => {
    setLoading(true);
    axios
      .get(`/device/${deviceUuid}/config_backup/list`)
      .then((response) => {
        setBackups(response.data.backups || []);
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Failed to fetch backups");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [deviceUuid, setError]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const uploadBackup = () => {
    if (!selectedFile) {
      setError("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    setUploading(true);
    setUploadProgress(0);

    axios
      .post(`/device/${deviceUuid}/config_backup/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      })
      .then((response) => {
        setMessage("Configuration backup uploaded successfully");
        setSelectedFile(null);
        setUploadProgress(0);
        // Reset file input
        const fileInput = document.getElementById("config-file-input");
        if (fileInput) fileInput.value = "";
        fetchBackups(); // Refresh the list
      })
      .catch((error) => {
        setError(
          error?.response?.data?.detail ||
            "Failed to upload configuration backup"
        );
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const createBackup = () => {
    setLoading(true);
    axios
      .post(`/device/${deviceUuid}/config_backup/create`)
      .then((response) => {
        setMessage("Configuration backup created successfully");
        setCreateBackupDialog(false);
        fetchBackups();
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Failed to create backup");
      })
      .finally(() => {
        setLoading(false);
      });
  };


  const downloadBackup = (download_link) => {
    try {
      // Create a temporary anchor element and trigger download
      const link = document.createElement("a");
      link.href = download_link;
      link.target = "_blank"; // Always open in new tab
      
      // Try to extract filename from the link, fallback to default
      const urlParts = download_link.split("/");
      const filename = urlParts[urlParts.length - 1] || "backup.cfg";
      
      // Set download attribute to force download if it's a file
      link.download = filename;
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Also open in new tab as backup
      window.open(download_link, "_blank");
    } catch (error) {
      console.error("Download failed:", error);
      setError("Failed to download file");
    }
  };

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  if (loading) {
    return <Loader />;
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Configuration Backups</Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUpload />}
            onClick={() => setCreateBackupDialog(true)}
            disabled={loading}
            style={{ marginRight: 8 }}
          >
            Create Backup
          </Button>
          {/* <Button
            variant="outlined"
            color="primary"
            startIcon={<Settings />}
            onClick={() => setReconfigDialog(true)}
            disabled={loading}
            style={{ marginRight: 8 }}
          >
            Reconfigure Device
          </Button> */}
          <IconButton onClick={fetchBackups} disabled={loading}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Upload Section */}
      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Configuration Backup
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <input
                accept=".cfg,.bak,.json"
                style={{ display: "none" }}
                id="config-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="config-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                >
                  Select Configuration File
                </Button>
              </label>
            </Grid>
            {selectedFile && (
              <Grid item>
                <Typography variant="body2">
                  Selected: {selectedFile.name}
                </Typography>
              </Grid>
            )}
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={uploadBackup}
                disabled={!selectedFile || uploading}
                startIcon={<CloudUpload />}
              >
                {uploading ? "Uploading..." : "Upload Configuration"}
              </Button>
            </Grid>
          </Grid>
          {uploading && (
            <Box mt={2}>
              <Typography variant="body2" gutterBottom>
                Uploading... {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Filename</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {backups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No configuration backups found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              backups.map((backup) => (
                <TableRow key={backup.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {backup.download_link
                        ? backup.download_link.split("/").pop()
                        : backup.filename}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {moment(backup.timestamp).local().format("lll")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {backup.download_link && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => downloadBackup(backup.download_link)}
                          >
                            <GetApp />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Backup Dialog */}
      <Dialog
        open={createBackupDialog}
        onClose={() => setCreateBackupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Configuration Backup</DialogTitle>
        <DialogContent>
          <Typography>
            This will create a backup of the current device configuration. Are
            you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateBackupDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={createBackup}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Create Backup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconfigure Device Dialog */}
      {/* <Dialog
        open={reconfigDialog}
        onClose={() => setReconfigDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reconfigure Device</DialogTitle>
        <DialogContent>
          <Typography>
            This will reconfigure the device with current settings. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReconfigDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={reconfigDevice}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Reconfigure
          </Button>
        </DialogActions>
      </Dialog> */}
    </Box>
  );
};

export default ConfigBackupList;
